import type { PayexThunkAction } from 'store/configureStore';

import { USE_PAYEX_PAYMENTS_CONTROLLER } from 'constants/features';
import * as ACTION_TYPES from 'constants/smsNotifications';
import { SET_FIELD_VALUE } from 'constants/ui';
import type { RootState } from 'reducers/types';
import { getFeature, getFieldValue, getLinkById, isFetching } from 'selectors';

import { normalizeSmsNotifications } from 'services/smsNotifications/smsNotifications';

import { updatePayment } from '../payments';

const shouldUsePayexService = (state: RootState) =>
  getFeature(state, USE_PAYEX_PAYMENTS_CONTROLLER);

export const enableSenderSMSNotification =
  (): PayexThunkAction =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();

    const service = shouldUsePayexService(state) ? payex : apiV3;

    const number = getFieldValue(state, 'sender_phone');

    dispatch({ type: ACTION_TYPES.RESET_RECEIVE_SMS_NOTIFICATIONS });

    const isFetchingPaymentActions = isFetching(state, 'paymentActions');
    if (isFetchingPaymentActions) {
      return;
    }

    const url = getUpdateSMSNotificationUrl(state);
    if (!url) {
      return;
    }

    dispatch({ type: ACTION_TYPES.ENABLE_SENDER_SMS_NOTIFICATION_REQUEST });

    try {
      const response = await service.payments.updateSMSNotification({
        active: true,
        number: number as string,
        url,
      });

      return dispatch({
        type: ACTION_TYPES.ENABLE_SENDER_SMS_NOTIFICATION_SUCCESS,
        payload: response,
      });
    } catch (error) {
      dispatch({ type: ACTION_TYPES.ENABLE_SENDER_SMS_NOTIFICATION_FAILURE });

      throw error;
    }
  };

export const updateSMSNotification =
  ({ number, active }: { number: string; active: boolean }): PayexThunkAction =>
  async (dispatch, getState, { apiV3, payex }) => {
    const state = getState();

    const service = shouldUsePayexService(state) ? payex : apiV3;

    const isFetchingPaymentActions = isFetching(state, 'paymentActions');
    if (isFetchingPaymentActions) {
      return;
    }

    const url = getUpdateSMSNotificationUrl(state);
    if (!url) {
      return;
    }

    dispatch({ type: ACTION_TYPES.UPDATE_SMS_NOTIFICATION_REQUEST });

    try {
      const response = await service.payments.updateSMSNotification({
        active,
        number,
        url,
      });

      if (active) {
        await dispatch({
          type: SET_FIELD_VALUE,
          payload: { name: 'sender_phone', value: number },
        });
        await dispatch(updatePayment());
      }

      return dispatch({
        type: ACTION_TYPES.UPDATE_SMS_NOTIFICATION_SUCCESS,
        payload: normalizeSmsNotifications(response),
      });
    } catch (error) {
      dispatch({ type: ACTION_TYPES.UPDATE_SMS_NOTIFICATION_FAILURE });

      throw error;
    }
  };

const getUpdateSMSNotificationUrl = (state: RootState) => {
  const link = getLinkById(state, 'sms_notification');
  if (!link) {
    return;
  }

  const { href } = link;
  return href;
};
